'use strict';

import { TRIAL_TAG_FIELDS } from './trialTagFields';

/**
 * Per-account vocabulary for Record and Edit Tags field suggestions.
 *
 * The fields ship with no preset entries, so a value becomes a suggestion the
 * first time someone enters it and is offered from then on. Keyed by account so
 * two clinicians sharing a machine do not inherit each other's terms.
 *
 * Comments is deliberately excluded: it is prose about one trial, not a term
 * worth offering again.
 *
 * When learning many fields at once (e.g. saving the Edit Tags modal), use
 * rememberTagValues so every field is folded into one persist. Calling
 * rememberTagValue in a loop with a stale memory object overwrites storage
 * and leaves only the last field.
 */

const STORAGE_PREFIX = 'evovision:trialTagVocabulary';
const MAX_VALUES_PER_FIELD = 30;

const REMEMBERED_KEYS = TRIAL_TAG_FIELDS
    .filter((field) => !field.derived && (field.type === 'select' || field.type === 'tags'))
    .map((field) => field.key);

export function isRememberedField(fieldKey) {
    return REMEMBERED_KEYS.includes(fieldKey);
}

export function tagMemoryKey(account) {
    const id = typeof account === 'string' ? account.trim().toLowerCase() : '';
    return `${STORAGE_PREFIX}:${id || 'default'}`;
}

/** The signed-in account, stored at login. Empty in builds that bypass login. */
export function currentAccountKey() {
    try {
        return localStorage.getItem('email') || '';
    } catch (err) {
        return '';
    }
}

export function loadTagMemory(account) {
    let raw = null;
    try {
        raw = localStorage.getItem(tagMemoryKey(account));
    } catch (err) {
        return {};
    }
    if (!raw) return {};
    let parsed = null;
    try {
        parsed = JSON.parse(raw);
    } catch (err) {
        return {};
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const memory = {};
    REMEMBERED_KEYS.forEach((key) => {
        if (!Array.isArray(parsed[key])) return;
        const values = parsed[key]
            .filter((value) => typeof value === 'string' && value.trim() !== '')
            .slice(0, MAX_VALUES_PER_FIELD);
        if (values.length) memory[key] = values;
    });
    return memory;
}

function persist(account, memory) {
    try {
        localStorage.setItem(tagMemoryKey(account), JSON.stringify(memory));
    } catch (err) {
        // Suggestions are a convenience; a full or blocked store must not break tagging.
    }
}

/** Most-recently-used first, deduped case-insensitively, keeping the entered casing. */
function promote(values, value) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (!trimmed) return values;
    const lower = trimmed.toLowerCase();
    const rest = values.filter((entry) => entry.toLowerCase() !== lower);
    const next = [trimmed, ...rest].slice(0, MAX_VALUES_PER_FIELD);
    const unchanged = next.length === values.length && next.every((entry, i) => entry === values[i]);
    return unchanged ? values : next;
}

/** Learn one field into memory without writing storage. */
function learnInto(memory, fieldKey, value) {
    if (!isRememberedField(fieldKey)) return memory;
    const current = memory || {};
    const incoming = Array.isArray(value) ? value : [value];
    let values = current[fieldKey] || [];
    incoming.forEach((entry) => {
        values = promote(values, entry);
    });
    if (values === (current[fieldKey] || [])) return memory;
    return { ...current, [fieldKey]: values };
}

/**
 * Learns the values just entered for a field. Returns the updated memory, or the
 * same object when there was nothing new, so callers can skip a re-render.
 */
export function rememberTagValue(account, memory, fieldKey, value) {
    const next = learnInto(memory, fieldKey, value);
    if (next === memory) return memory;
    persist(account, next);
    return next;
}

/**
 * Learns many fields in one pass and persists once. Use this when saving a full
 * tag form so earlier fields are not wiped by later writes.
 */
export function rememberTagValues(account, memory, valuesByKey) {
    if (!valuesByKey || typeof valuesByKey !== 'object') return memory || {};
    let next = memory || {};
    let changed = false;
    Object.keys(valuesByKey).forEach((fieldKey) => {
        const learned = learnInto(next, fieldKey, valuesByKey[fieldKey]);
        if (learned !== next) {
            next = learned;
            changed = true;
        }
    });
    if (!changed) return memory || {};
    persist(account, next);
    return next;
}

export function suggestionsFor(memory, fieldKey) {
    if (!memory) return [];
    const values = memory[fieldKey];
    return Array.isArray(values) ? values : [];
}
